import React, { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import ReCAPTCHAComponent from "react-google-recaptcha";
import { sendLoginUsername, sendLoginPasscode } from "@/dataLayer";
import { Merchant } from "@/dto/merchant";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { RegularModal } from "../modals/modals";
import { LoginModalUsernameInput } from "./loginModalUsernameInput";
import { LoginModalOTPInput } from "./loginModalOTPInput";
import { useLocale, useTranslations } from "next-intl";
import { LeftArrowModalIcon } from "./leftArrowModalIcon";
import { useAuth } from "@/app/providers/authProvider";

export const LoginInfoModal = ({
  isOpen,
  onRequestClose,
  merchantColor,
  toCheckoutAfterLogin,
  merchant,
}: {
  merchant: Merchant;
  isOpen: boolean;
  onRequestClose: () => void;
  merchantColor: string;
  toCheckoutAfterLogin: boolean;
}) => {
  const t = useTranslations("EditContactInfoModal");
  const tAuthErrors = useTranslations("auth.errors");
  const locale = useLocale();
  const { setIsLoggedIn } = useAuth();
  const router = useRouter();
  const [showVerification, setShowVerification] = useState<boolean>(false);
  const [showPhoneNumberInput, setShowPhoneNumberInput] =
    useState<boolean>(true);
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const captchaRef = useRef<ReCAPTCHAComponent | null>(null);
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CONSUMER_WEB_KEY || "";
  const serviceCart = getFromLocalStorage(LocalStorageKey.SERVICE_CART, []);
  const showGuestCheckout =
    (merchant.isRetail || merchant.isRestaurant || serviceCart?.length === 0) &&
    toCheckoutAfterLogin;
  const phoneField = "phone";
  const emailField = "email";
  const showEmailSwitching = merchant?.enabledSettings?.includes(
    "EMAIL_SIGNUP_LOGIN_ON_COLO2"
  );
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const subtitleId = `${baseId}-subtitle`;

  useEffect(() => {
    if (code.length === 6) {
      setIsLoading(true);
      setErrorMessage(null);
      confirmCode();
    }
  }, [code]);

  useEffect(() => {
    if (isOpen && toCheckoutAfterLogin) {
      router.prefetch("/checkout");
    }
  }, [isOpen, toCheckoutAfterLogin]);

  const form = useForm({
    defaultValues: {
      countryCode: "",
      phone: "",
      formattedPhoneNumber: "",
      email: "",
    },
    mode: "all",
  });

  const {
    formState: { isValid },
    getValues,
    setValue,
    reset,
  } = form;

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleLogin = async (token: string) => {
    setIsLoading(true);

    const username = showPhoneNumberInput
      ? getValues("formattedPhoneNumber")
      : getValues("email");

    try {
      const result = await sendLoginUsername(username, token);

      if (result.status === 204) {
        setShowVerification(true);
        setErrorMessage(null);
      } else {
        const errorCode =
          result.data?.errorCondition || result.data?.message || "UNKNOWN";

        const hasTranslation = errorCode && tAuthErrors.has(errorCode);

        const translatedMessage = hasTranslation
          ? tAuthErrors(errorCode, { contact: username })
          : t("captchaError");

        setErrorMessage(translatedMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  async function onRecaptchaSubmit(value: any) {
    if (!value) {
      return;
    }
    await handleLogin(value);
  }

  const confirmCode = async () => {
    let username = showPhoneNumberInput
      ? getValues("formattedPhoneNumber")
      : getValues("email");

    try {
      const result = await sendLoginPasscode(username, code);

      if (
        result?.globalCustomerUuid &&
        result?.sessionToken &&
        result?.refreshToken
      ) {
        setCode("");
        setIsLoggedIn(true);
        if (toCheckoutAfterLogin) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          router.push("/checkout");
        } else {
          setShowVerification(false);
          setIsLoading(false);
          onRequestClose();
        }
        return;
      }

      const errorCode = result?.errorCondition;
      const hasTranslation = errorCode && tAuthErrors.has(errorCode);

      const translatedMessage = hasTranslation
        ? tAuthErrors(errorCode, { contact: username })
        : t("otpError");

      setErrorMessage(translatedMessage);
      setIsLoading(false);
    } catch (error) {
      setErrorMessage(t("unexpectedError"));
      setIsLoading(false);
      return error;
    }
  };

  const handleGuestCheckout = () => {
    if (toCheckoutAfterLogin) {
      router.push("/guest-checkout");
    } else {
      onRequestClose();
    }
  };

  const handleClose = () => {
    setValue(phoneField, "");
    setValue(emailField, "");
    form.clearErrors();
    setErrorMessage(null);

    setShowVerification(false);
    setCode("");

    onRequestClose();
  };

  const handleBack = () => {
    setErrorMessage(null);
    setShowVerification(false);
    setCode("");
  };

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
      focusElementId={titleId}
      aria={{
        labelledby: titleId,
        describedby: subtitleId,
      }}
      contentClassName="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden p-4 [@media(max-height:480px)]:h-[100%] [@media(max-height:480px)]:overflow-auto flex flex-col-reverse justify-end"
    >
      {!showVerification && (
        <LoginModalUsernameInput
          merchant={merchant}
          form={form}
          setErrorMessage={setErrorMessage}
          merchantColor={merchantColor}
          showPhoneNumberInput={showPhoneNumberInput}
          showEmailSwitching={showEmailSwitching}
          setShowPhoneNumberInput={setShowPhoneNumberInput}
          errorMessage={errorMessage}
          isLoading={isLoading}
          isValid={isValid}
          captchaRef={captchaRef}
          showGuestCheckout={showGuestCheckout}
          handleGuestCheckout={handleGuestCheckout}
          onLogin={handleLogin}
          recaptchaEnabled={!!recaptchaKey}
          titleId={titleId}
          subtitleId={subtitleId}
        />
      )}
      {showVerification && (
        <LoginModalOTPInput
          setErrorMessage={setErrorMessage}
          merchantColor={merchantColor}
          showPhoneNumberInput={showPhoneNumberInput}
          errorMessage={errorMessage}
          isLoading={isLoading}
          captchaRef={captchaRef}
          setCode={setCode}
          getValues={getValues}
          code={code}
          titleId={titleId}
          subtitleId={subtitleId}
        />
      )}
      <div className="flex justify-between items-center px-6 pt-6 shrink-0">
        <div className="w-8">
          {showVerification && (
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-900"
              aria-label={t("goBack")}
            >
              <LeftArrowModalIcon />
            </button>
          )}
        </div>
      </div>
      {recaptchaKey && (
        <ReCAPTCHAComponent
          className=""
          sitekey={recaptchaKey}
          ref={captchaRef}
          onChange={onRecaptchaSubmit}
          size="invisible"
          badge="bottomright"
          hl={locale}
        />
      )}
    </RegularModal>
  );
};
